import { useMemo } from 'react'
import { useProjektprognosCashflow } from './useCashflow'
import { usePeriodReportSnapshotData } from '../usePeriodReportSnapshotData'
import { usePeriodReportSnapshots } from '../usePeriodReportSnapshots'
import { useProjektprognosEditingContext } from '../../../context/ProjektprognosEditingContext'
import { getReportSnapshotIds } from '../../../utils/getReportSnapshotIds'
import { formatData } from '../../../../../utils/formatData'
import { GridRow } from '../../../../../interfaces/grids/GridRow'
import { GridRows } from '../../../../../interfaces/grids/GridRows'

export const useCashflowReportData = () => {
  const { selectedPeriodReport } = useProjektprognosEditingContext()

  const { data: periodReports, isSuccess: isPeriodReportsSuccess } =
    usePeriodReportSnapshots()
  const hasPeriodReports = (periodReports?.length ?? 0) > 0

  const { previousSnapshotId, currentSnapshotId } = useMemo(
    () => getReportSnapshotIds(selectedPeriodReport),
    [selectedPeriodReport]
  )

  const { previousSnapshot, currentSnapshot } = usePeriodReportSnapshotData(
    previousSnapshotId,
    currentSnapshotId
  )

  // Only fetch live data when: projectForecast exists, there is a previousSnapshot
  // to compare with, and there is no currentSnapshot (i.e. not viewing a closed report)
  const liveCashflow = useProjektprognosCashflow({
    enabled:
      isPeriodReportsSuccess &&
      hasPeriodReports &&
      !currentSnapshotId &&
      !!previousSnapshotId,
  })

  // Rows: strict separation + enrichment with previous prognosis values
  const rows = useMemo<GridRows | undefined>(() => {
    // Strict separation: snapshot data when currentSnapshotId exists, live otherwise
    const mainData = currentSnapshotId
      ? currentSnapshot.data?.cashflowSheet?.data
        ? formatData(currentSnapshot.data.cashflowSheet.data)
        : undefined
      : liveCashflow.data

    if (!mainData) return undefined

    // Previous prognosis: always from previousSnapshot
    const previousSnapshotCashflow = previousSnapshot.data?.cashflowSheet?.data
    if (!previousSnapshotCashflow || previousSnapshotCashflow.length === 0) {
      // No previous snapshot — set all differences to null
      const clearDifference = (row: GridRow): GridRow => ({
        ...row,
        difference: null,
      })

      return {
        prefixedData: mainData.prefixedData.map(clearDifference),
        emptyRowsHidden: mainData.emptyRowsHidden.map(clearDifference),
      }
    }

    // Format previous snapshot data for comparison
    const formattedPreviousSnapshot = formatData(previousSnapshotCashflow)

    // Create a Map for quick lookup based on accountId
    const snapshotMap = new Map<number, GridRow>()
    formattedPreviousSnapshot.prefixedData.forEach((row: GridRow) => {
      const accountId = row.accountId as number | undefined
      if (accountId) {
        snapshotMap.set(accountId, row)
      }
    })

    // Enrich a row with previous prognosis values and per-month differences
    const enrichRow = (row: GridRow): GridRow => {
      const enrichedRow: GridRow = { ...row, difference: null }

      const accountId = row.accountId as number | undefined
      if (!accountId) return enrichedRow

      const snapshotRow = snapshotMap.get(accountId)
      if (!snapshotRow) return enrichedRow

      // Add previous total prognosis
      const previousTotalPrognosis = snapshotRow.totalPrognosis as
        | number
        | null
        | undefined
      if (
        previousTotalPrognosis !== undefined &&
        previousTotalPrognosis !== null
      ) {
        enrichedRow.previousTotalPrognosis = previousTotalPrognosis
      }

      // Calculate difference as current prognosis - previous prognosis
      const currentPrognosis = row.totalPrognosis as number | null | undefined
      const hasCurrent =
        currentPrognosis !== undefined && currentPrognosis !== null
      const hasPrevious =
        previousTotalPrognosis !== undefined && previousTotalPrognosis !== null

      if (hasCurrent || hasPrevious) {
        const curr = Number(currentPrognosis ?? 0)
        const prev = Number(previousTotalPrognosis ?? 0)
        const isKostnader =
          (row.grandParentDisplayName as string)?.toUpperCase() === 'KOSTNADER'
        enrichedRow.difference = isKostnader ? prev - curr : curr - prev
      } else {
        enrichedRow.difference = null
      }

      return enrichedRow
    }

    return {
      prefixedData: mainData.prefixedData.map(enrichRow),
      emptyRowsHidden: mainData.emptyRowsHidden.map(enrichRow),
    }
  }, [
    currentSnapshotId,
    liveCashflow.data,
    previousSnapshot.data,
    currentSnapshot.data,
  ])

  // Previous snapshot name for column header
  const previousSnapshotName = useMemo<string | undefined>(() => {
    if (!selectedPeriodReport) return undefined

    const previousSnapshotRef =
      selectedPeriodReport.snapshotReferences?.previousSnapshotReference

    if (previousSnapshotRef) {
      return `${previousSnapshotRef.reportPeriod} ${previousSnapshotRef.year}`
    }

    return undefined
  }, [selectedPeriodReport])

  // Loading: combine previous + current (snapshot or live)
  const currentIsLoading = currentSnapshotId
    ? currentSnapshot.isLoading || currentSnapshot.isFetching
    : liveCashflow.isLoading

  const isLoading =
    previousSnapshot.isLoading ||
    previousSnapshot.isFetching ||
    currentIsLoading

  const error =
    previousSnapshot.error ||
    (currentSnapshotId ? currentSnapshot.error : liveCashflow.error) ||
    null

  return {
    rows,
    previousSnapshotName,
    isLoading,
    error,
  }
}
